import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, BookOpen, StickyNote, GraduationCap, Briefcase, GitBranch,
  HelpCircle, AlignLeft, ListChecks, Mail, ChevronRight, Star,
  Pencil, Plus, ArrowUp, ArrowDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { apiRequest } from "@/lib/queryClient";
import { LOADOUT_AGENT_CATALOG, LOADOUT_AGENT_IDS } from "../../../shared/profilePresets";

const ICON_MAP: Record<string, React.ElementType> = {
  Search, BookOpen, StickyNote, GraduationCap, Briefcase, GitBranch,
  HelpCircle, AlignLeft, ListChecks, Mail, ChevronRight, Star,
};

function AgentIcon({ iconName, className }: { iconName: string; className?: string }) {
  const Icon = ICON_MAP[iconName] || Search;
  return <Icon className={className} />;
}

interface LoadoutPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSave: (ids: string[]) => void;
}

function LoadoutPickerDialog({ open, onOpenChange, selectedIds, onSave }: LoadoutPickerDialogProps) {
  const [chosen, setChosen] = useState<string[]>(selectedIds);

  function toggle(id: string) {
    setChosen((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  }

  function moveUp(idx: number) {
    if (idx === 0) return;
    setChosen((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }

  function moveDown(idx: number) {
    setChosen((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }

  function handleOpen(isOpen: boolean) {
    if (isOpen) setChosen(selectedIds);
    onOpenChange(isOpen);
  }

  const modes = LOADOUT_AGENT_IDS.filter((id) => LOADOUT_AGENT_CATALOG[id].category === "mode");
  const tools = LOADOUT_AGENT_IDS.filter((id) => LOADOUT_AGENT_CATALOG[id].category === "tool");

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-sm" data-testid="loadout-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choose your loadout</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Select up to 3 · <span className="font-medium">{chosen.length}/3 selected</span>
        </p>

        {chosen.length > 0 && (
          <div className="rounded-md border bg-muted/30 p-2 space-y-1">
            <p className="text-xs text-muted-foreground font-medium mb-1">Order</p>
            {chosen.map((id, idx) => {
              const agent = LOADOUT_AGENT_CATALOG[id];
              return (
                <div key={id} className="flex items-center gap-2">
                  <AgentIcon iconName={agent.iconName} className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="flex-1 text-sm">{agent.name}</span>
                  <button
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => moveDown(idx)}
                    disabled={idx === chosen.length - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="max-h-52 overflow-y-auto space-y-4">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Modes
            </p>
            <div className="space-y-1">
              {modes.map((id) => {
                const agent = LOADOUT_AGENT_CATALOG[id];
                const isChecked = chosen.includes(id);
                const disabled = !isChecked && chosen.length >= 3;
                return (
                  <div key={id} className="flex items-start gap-2.5 rounded-md p-1.5 hover:bg-muted">
                    <Checkbox
                      id={`loadout-${id}`}
                      checked={isChecked}
                      disabled={disabled}
                      onCheckedChange={() => toggle(id)}
                      data-testid={`loadout-checkbox-${id}`}
                    />
                    <Label htmlFor={`loadout-${id}`} className="flex-1 cursor-pointer">
                      <span className="flex items-center gap-1.5 font-medium text-sm">
                        <AgentIcon iconName={agent.iconName} className="h-3.5 w-3.5" />
                        {agent.name}
                      </span>
                      <p className="text-xs text-muted-foreground font-normal">{agent.description}</p>
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Tools
            </p>
            <div className="space-y-1">
              {tools.map((id) => {
                const agent = LOADOUT_AGENT_CATALOG[id];
                const isChecked = chosen.includes(id);
                const disabled = !isChecked && chosen.length >= 3;
                return (
                  <div key={id} className="flex items-start gap-2.5 rounded-md p-1.5 hover:bg-muted">
                    <Checkbox
                      id={`loadout-${id}`}
                      checked={isChecked}
                      disabled={disabled}
                      onCheckedChange={() => toggle(id)}
                      data-testid={`loadout-checkbox-${id}`}
                    />
                    <Label htmlFor={`loadout-${id}`} className="flex-1 cursor-pointer">
                      <span className="flex items-center gap-1.5 font-medium text-sm">
                        <AgentIcon iconName={agent.iconName} className="h-3.5 w-3.5" />
                        {agent.name}
                      </span>
                      <p className="text-xs text-muted-foreground font-normal">{agent.description}</p>
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSave(chosen); onOpenChange(false); }} data-testid="save-loadout-btn">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface LoadoutStripProps {
  selectedIds: string[];
}

export function LoadoutStrip({ selectedIds }: LoadoutStripProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (ids: string[]) =>
      apiRequest("PATCH", "/api/user-profile", { loadoutAgentPresetIds: ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personalised-profile"] });
    },
  });

  return (
    <div data-testid="loadout-strip">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
          Current loadout
        </span>
        <button
          onClick={() => setPickerOpen(true)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          data-testid="edit-loadout-btn"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>

      {selectedIds.length === 0 ? (
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground italic flex-1">
            Choose up to 3 agents to feature here
          </p>
          <button
            onClick={() => setPickerOpen(true)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="add-loadout-btn"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {selectedIds.map((id) => {
            const agent = LOADOUT_AGENT_CATALOG[id];
            if (!agent) return null;
            return (
              <span
                key={id}
                data-testid={`loadout-chip-${id}`}
                className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-sm"
              >
                <AgentIcon iconName={agent.iconName} className="h-3.5 w-3.5 text-muted-foreground" />
                {agent.name}
              </span>
            );
          })}
        </div>
      )}

      <LoadoutPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selectedIds={selectedIds}
        onSave={(ids) => mutation.mutate(ids)}
      />
    </div>
  );
}
